/**
 * Deal Room Template API Client
 * 
 * Handles all API interactions for Deal Room Template management.
 * Templates define pre-configured layouts for collaborative deal workspaces.
 */

import { baseClient } from './baseClient';
import type {
  DealRoomTemplate,
  DealRoomTemplateCreate,
  DealRoomTemplateUpdate,
  DealRoomSettings,
  DealRoomSettingsCreate,
  DealRoomAccess,
  DealRoomAccessCreate,
} from '../types/dealRoom';

const BASE_URL = '/deal-room';

/**
 * Deal Room Templates
 */

export const getDealRoomTemplates = async (
  activeOnly: boolean = true
): Promise<DealRoomTemplate[]> => {
  const params = new URLSearchParams();
  if (activeOnly) {
    params.append('active_only', 'true');
  }
  
  return await baseClient.get<DealRoomTemplate[]>(
    `${BASE_URL}/templates?${params.toString()}`
  );
};

export const getDealRoomTemplate = async (
  templateId: string
): Promise<DealRoomTemplate> => {
  return await baseClient.get<DealRoomTemplate>(
    `${BASE_URL}/templates/${templateId}`
  );
};

export const createDealRoomTemplate = async (
  template: DealRoomTemplateCreate
): Promise<DealRoomTemplate> => {
  return await baseClient.post<DealRoomTemplate>(
    `${BASE_URL}/templates`,
    template
  );
};

export const updateDealRoomTemplate = async (
  templateId: string,
  updates: DealRoomTemplateUpdate
): Promise<DealRoomTemplate> => {
  return await baseClient.put<DealRoomTemplate>(
    `${BASE_URL}/templates/${templateId}`,
    updates
  );
};

/**
 * Deal Room Settings (per-deal configuration)
 */

export const getDealRoomSettings = async (
  dealId: string
): Promise<DealRoomSettings> => {
  return await baseClient.get<DealRoomSettings>(
    `${BASE_URL}/settings/deal/${dealId}`
  );
};

export const createDealRoomSettings = async (
  settings: DealRoomSettingsCreate
): Promise<DealRoomSettings> => {
  return await baseClient.post<DealRoomSettings>(
    `${BASE_URL}/settings`,
    settings
  );
};

export const updateDealRoomSettings = async (
  settingsId: string,
  updates: Partial<DealRoomSettings>
): Promise<DealRoomSettings> => {
  return await baseClient.put<DealRoomSettings>(
    `${BASE_URL}/settings/${settingsId}`,
    updates
  );
};

/**
 * Deal Room Access Control
 */

export const getDealRoomAccessList = async (
  dealId: string
): Promise<DealRoomAccess[]> => {
  return await baseClient.get<DealRoomAccess[]>(
    `${BASE_URL}/access/deal/${dealId}`
  );
};

export const grantDealRoomAccess = async (
  access: DealRoomAccessCreate
): Promise<DealRoomAccess> => {
  return await baseClient.post<DealRoomAccess>(
    `${BASE_URL}/access`,
    access
  );
};

export const updateDealRoomAccess = async (
  accessId: string,
  updates: Partial<DealRoomAccess>
): Promise<DealRoomAccess> => {
  return await baseClient.put<DealRoomAccess>(
    `${BASE_URL}/access/${accessId}`,
    updates
  );
};

export const revokeDealRoomAccess = async (
  accessId: string
): Promise<void> => {
  await baseClient.delete(`${BASE_URL}/access/${accessId}`);
};
